import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authApi, guardarSesion, limpiarSesion } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await authApi.me();
      setUser(r.user);
      setCompany(r.company);
      setStore(r.store);
      localStorage.setItem("aipos_user", JSON.stringify(r.user));
      localStorage.setItem("aipos_company", JSON.stringify(r.company || null));
      localStorage.setItem("aipos_store", JSON.stringify(r.store || null));
    } catch (e) {
      // Sin red (p.ej. el equipo arrancó sin Internet): mantenemos la última sesión conocida
      // para que el POS pueda seguir vendiendo offline. Si el servidor respondió (401/403) se cierra.
      const offline = !e?.response;
      let cached = null;
      try { cached = JSON.parse(localStorage.getItem("aipos_user") || "null"); } catch (_) { cached = null; }
      if (offline && cached) {
        setUser(cached);
        try { setCompany(JSON.parse(localStorage.getItem("aipos_company") || "null")); } catch (_) { setCompany(null); }
        try { setStore(JSON.parse(localStorage.getItem("aipos_store") || "null")); } catch (_) { setStore(null); }
      } else {
        setUser(null);
        setCompany(null);
        setStore(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback (hash contains session_id), skip /me check
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    if (localStorage.getItem("aipos_token") || localStorage.getItem("aipos_refresh") || document.cookie.includes("session_token")) {
      refresh();
    } else {
      setLoading(false);
    }
  }, [refresh]);

  const login = async (email, password) => {
    const r = await authApi.login({ email, password });
    // Clave dinámica pendiente: todavía no hay sesión
    if (r.mfa_required || r.mfa_setup_required) return r;
    guardarSesion(r);
    await refresh();
    return r;
  };

  const applyToken = async (token, refresh_token) => {
    guardarSesion({ access_token: token, refresh_token });
    await refresh();
  };

  const register = async (data) => {
    const r = await authApi.register(data);
    guardarSesion(r);
    await refresh();
    return r;
  };

  const logout = async () => {
    try { await authApi.logout(); } catch(_){}
    limpiarSesion();
    localStorage.removeItem("aipos_company");
    localStorage.removeItem("aipos_store");
    setUser(null); setCompany(null); setStore(null);
  };

  return (
    <AuthCtx.Provider value={{ user, company, store, loading, login, register, logout, refresh, setStore, applyToken }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
